import { useQueryClient } from '@tanstack/react-query';
import { ZfLoadingSpinner } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { Suspense, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useYwuApp } from '../../../app/contexts/ywu-app-context';
import { showErrorToast, showSuccessToast } from '../../../app/utils/toastUtils';
import { useWebComponentEvents } from '../../hooks';
import { refetchFamilyTreeAfterRelationChange } from '../../utils/refetch-family-tree-after-relation-change';
import { PartnerRelationModalWidget } from './PcmPartnerRelationModalWidget';

interface IDeletePartnerRelationModalProps {
    mainPartnerUuid: string;
    partnerUuid: string;
    onModalClose: () => void;
}

export const DeletePartnerRelationModal: FunctionComponent<IDeletePartnerRelationModalProps> = ({ mainPartnerUuid, partnerUuid, onModalClose }) => {
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const { isPresentationMode } = useYwuApp();

    const modalRef = useRef<HTMLElement>(null);
    const [isModalReady, setIsModalReady] = useState<boolean>(false);

    useWebComponentEvents(modalRef, isModalReady, [
        {
            eventName: 'PCM_DELETE_PARTNER_RELATION_MODAL_CLOSE',
            eventCallback: () => {
                onModalClose();
                setIsModalReady(false);
            },
        },
        {
            eventName: 'PCM_DELETE_PARTNER_RELATION_SUCCESS',
            eventCallback: () => {
                showSuccessToast(t('removePartnerRelationSuccess'));
                refetchFamilyTreeAfterRelationChange(queryClient, mainPartnerUuid);
            },
        },
        {
            eventName: 'PCM_DELETE_PARTNER_RELATION_ERROR',
            eventCallback: () => showErrorToast({ userMessage: t('removePartnerRelationFailure') }),
        },
    ]);

    return createPortal(
        <Suspense fallback={<ZfLoadingSpinner style={{ margin: 'auto' }} />}>
            <PartnerRelationModalWidget
                ref={modalRef}
                modalType="DeletePartnerRelation"
                partnerUuid={partnerUuid}
                language={i18n.language}
                isPresentationMode={isPresentationMode}
                onModalReady={() => setIsModalReady(true)}
            />
        </Suspense>,
        document.body,
    );
};
